import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import HolidaysContext from '@totem/components/holidays/HolidaysContext';
import { Holiday, HolidayPayload } from '@totem/components/holidays/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { HOLIDAYS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  children: ReactNode;
};
const HolidaysContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${HOLIDAYS_ENDPOINT}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      });
      if (!CheckResponseShowError(response)) {
        setHolidays([]);
        return;
      }
      const result: Holiday[] = await response.json();
      setHolidays(Array.isArray(result) ? result : []);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  const upsertHoliday = useCallback(
    async (holiday: HolidayPayload) => {
      setSaving(true);
      try {
        const response = await fetch(`${HOLIDAYS_ENDPOINT}`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(holiday),
        });
        if (!CheckResponseShowError(response)) {
          return;
        }
        notification.success({
          title: holiday.id ? 'Holiday updated' : 'Holiday added',
        });

        setModalOpen(false);

        setEditingHoliday(null);

        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh],
  );
  const deleteHoliday = useCallback(
    async (holidayId: string) => {
      setSaving(true);
      try {
        const response = await fetch(`${HOLIDAYS_ENDPOINT}/${holidayId}`, {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        });
        if (!CheckResponseShowError(response)) {
          return;
        }
        notification.success({
          title: 'Holiday deleted',
        });

        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh],
  );
  const openCreateModal = () => {
    setEditingHoliday(null);

    setModalOpen(true);
  };
  const openEditModal = (holiday: Holiday) => {
    setEditingHoliday(holiday);

    setModalOpen(true);
  };
  const closeModal = () => {
    setEditingHoliday(null);

    setModalOpen(false);
  };
  return (
    <HolidaysContext.Provider
      value={{
        holidays,
        loading,
        saving,
        modalOpen,
        editingHoliday,
        refresh,
        openCreateModal,
        openEditModal,
        closeModal,
        upsertHoliday,
        deleteHoliday,
      }}
    >
      {children}
    </HolidaysContext.Provider>
  );
};
export default HolidaysContainer;
